import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  Chip,
  Stack,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  Tooltip,
  useMediaQuery,
  useTheme as muiTheme,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { dataGridStyle, palette } from "../../../../theme";
import API from "../../../../api";
import AddLecsReseller from "./AddLecsReseller";
import AddAccountDialog from "./AddAccountDialog";
import { mapTipoToDescrizione } from "../lecsType";
import { MUTED, SURFACE, SURFACE_HEAD, BORDER } from "../../../dashboard/dashboard-components/resellerUi";
import { startToast, updateToastSuccess, updateToastError } from '../../../../utils/notification-loading';

const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

// Azienda = ragione sociale dell'account primario (altrimenti del primo account).
const companyOf = (c) => {
  const users = toArray(c.utenti);
  return (users.find((u) => u.type === "admin") ?? users[0])?.ragioneSociale ?? "";
};

const daysColor = (d) => {
  const n = Number(d);
  if (d === null || d === undefined || !Number.isFinite(n)) return undefined;
  return n <= 0 ? palette.error.light : n <= 30 ? "#EF9F27" : undefined;
};

const ClientList = () => {
  const mui = muiTheme();
  const isFullScreen = useMediaQuery(mui.breakpoints.down("sm"));

  const [auth, setAuth] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localAdmins, setLocalAdmins] = useState([]);
  const [search, setSearch] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedMatricola, setSelectedMatricola] = useState(null);
  const [detailMatricola, setDetailMatricola] = useState(null);

  const fetchClients = useCallback(() => {
    return API.getLecsAccounts()
      .then((res) => setClients(res.data))
      .catch((err) => console.error("[ClientList] Failed to fetch accounts:", err))
      .finally(() => setLoading(false));
  }, []);

  // Le tre richieste partono insieme: la tabella non aspetta ruolo e local admin.
  useEffect(() => {
    fetchClients();

    API.getResType().then((res) => {
      const role = res.data.auth;
      setAuth(role);

      if (role === "super-admin") {
        API.getLocalAdmins({ type: "add" }).then((res) => {
          setLocalAdmins(
            res.data.map((admin) => ({ code: admin.code, label: `${admin.nome} ${admin.cognome}` }))
          );
        }).catch((err) => console.error("[ClientList] Failed to fetch local admins:", err));
      }
    }).catch((err) => console.error("[ClientList] Failed to fetch reseller type:", err));
  }, [fetchClients]);

  const adminLabel = useCallback(
    (code) => localAdmins.find((a) => a.code === code)?.label || code,
    [localAdmins]
  );

  const handleChangeLocalAdmin = (matricola, adminCodes) => {
    startToast();
    setClients((prev) =>
      prev.map((client) => (client.matricola === matricola ? { ...client, localAdmin: adminCodes } : client))
    );

    API.updateLocalAdmin({ matricola, adminCode: adminCodes })
      .then(() => updateToastSuccess("Local admin updated"))
      .catch(() => updateToastError("Error updating local admin"));
  };

  const handleAddAccount = (matricola) => {
    setSelectedMatricola(matricola);
    setOpenAddDialog(true);
  };

  const handleRemoveAccount = ({ matricola, email, type }) => {
    startToast();
    API.delAccountLecs({ serial_number: matricola, email, type })
      .then(() => {
        updateToastSuccess("Account removed successfully");
        fetchClients();
      })
      .catch(() => {
        updateToastError("Error removing account");
      });
  };

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients
      .filter((c) => {
        if (!q) return true;
        return (
          (c.nomeLecs || "").toLowerCase().includes(q) ||
          (c.matricola || "").toLowerCase().includes(q) ||
          companyOf(c).toLowerCase().includes(q) ||
          (c.dipartimentoSede || "").toLowerCase().includes(q) ||
          toArray(c.utenti).some(
            (u) => (u.email || "").toLowerCase().includes(q) || (u.ragioneSociale || "").toLowerCase().includes(q)
          )
        );
      })
      .map((c) => ({ ...c, id: c.matricola }));
  }, [clients, search]);

  const detail = useMemo(
    () => clients.find((c) => c.matricola === detailMatricola) || null,
    [clients, detailMatricola]
  );

  const columns = useMemo(() => {
    const muted = (t) => <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>{t}</Typography>;
    const cols = [
      { field: "matricola", headerName: "LECS", width: 110 },
      { field: "nomeLecs", headerName: "Name", flex: 1, minWidth: 150, renderCell: (p) => p.value || muted("-") },
      {
        field: "company",
        headerName: "Company",
        flex: 1.2,
        minWidth: 190,
        valueGetter: (_, row) => companyOf(row),
        renderCell: (p) => p.value || muted("No user found"),
      },
      { field: "dipartimentoSede", headerName: "Site", flex: 1, minWidth: 150, renderCell: (p) => p.value || muted("-") },
      {
        field: "tipo",
        headerName: "Model",
        width: 150,
        valueGetter: (_, row) => (row.tipo ? mapTipoToDescrizione(row.tipo) : ""),
        renderCell: (p) => p.value || muted("-"),
      },
      {
        field: "primary",
        headerName: "Primary account",
        flex: 1.2,
        minWidth: 210,
        valueGetter: (_, row) => toArray(row.utenti).find((u) => u.type === "admin")?.email ?? "",
        renderCell: (p) => p.value || muted("-"),
      },
      {
        field: "accounts",
        headerName: "Accounts",
        width: 100,
        type: "number",
        align: "left",
        headerAlign: "left",
        valueGetter: (_, row) => toArray(row.utenti).length,
        renderCell: (p) => (
          <Typography component="span" sx={{ fontSize: 13 }}>
            {p.value}
            {p.row.accountLimit != null && <Box component="span" sx={{ color: MUTED }}> / {p.row.accountLimit}</Box>}
          </Typography>
        ),
      },
      {
        field: "daysLeft",
        headerName: "Days left",
        width: 100,
        type: "number",
        align: "left",
        headerAlign: "left",
        renderCell: (p) =>
          p.value === null || p.value === undefined ? (
            muted("-")
          ) : (
            <Typography component="span" sx={{ fontSize: 13, color: daysColor(p.value) }}>{p.value}</Typography>
          ),
      },
    ];
    if (auth === "super-admin") {
      cols.push({
        field: "localAdmin",
        headerName: "Local admin",
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => toArray(row.localAdmin).map(adminLabel).join(", "),
        renderCell: (p) => p.value || muted("-"),
      });
    }
    return cols;
  }, [auth, adminLabel]);

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search LECS, name, account…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: 1, maxWidth: 420 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: MUTED }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Chip
          size="small"
          label={`${filteredClients.length}${filteredClients.length !== clients.length ? ` / ${clients.length}` : ""} devices`}
          sx={{ color: MUTED, backgroundColor: "transparent", border: "1px solid rgba(255,255,255,0.14)" }}
        />
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Add Device
        </Button>
      </Box>

      {/* Tabella (virtualizzata: costo costante col numero di LECS) */}
      <Box sx={{ height: "calc(100vh - 250px)", minHeight: 400 }}>
        <DataGridPro
          sx={{ ...dataGridStyle, "& .MuiDataGrid-row": { cursor: "pointer" } }}
          rows={filteredClients}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: "No devices found" }}
          onRowClick={(p) => setDetailMatricola(p.row.matricola)}
          initialState={{ sorting: { sortModel: [{ field: "nomeLecs", sort: "asc" }] } }}
        />
      </Box>

      {/* Dettaglio LECS: montato solo per il LECS aperto */}
      <Drawer
        anchor="right"
        open={Boolean(detail)}
        onClose={() => setDetailMatricola(null)}
        slotProps={{ paper: { sx: { width: { xs: "100%", sm: 460 }, backgroundColor: SURFACE, backgroundImage: "none", borderLeft: `1px solid ${BORDER}` } } }}
      >
        {detail && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 2, py: 1.5, backgroundColor: SURFACE_HEAD }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: palette.primary.light }}>{detail.matricola}</Typography>
                <Typography sx={{ fontSize: 13, color: MUTED }} noWrap>{detail.nomeLecs || "-"}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setDetailMatricola(null)} aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ p: 2, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                {[
                  ["Company", companyOf(detail)],
                  ["Site", detail.dipartimentoSede],
                  ["Model", detail.tipo ? mapTipoToDescrizione(detail.tipo) : ""],
                  ["Days left", detail.daysLeft],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
                    <Typography
                      sx={{ fontSize: 14, color: label === "Days left" ? daysColor(value) : undefined }}
                      noWrap
                    >
                      {value === null || value === undefined || value === "" ? "-" : value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {auth === "super-admin" && (
                <FormControl fullWidth size="small">
                  <InputLabel id="local-admin-label">Local Admin</InputLabel>
                  <Select
                    labelId="local-admin-label"
                    multiple
                    value={toArray(detail.localAdmin)}
                    label="Local Admin"
                    onChange={(e) => handleChangeLocalAdmin(detail.matricola, e.target.value)}
                    renderValue={(selected) => selected.map(adminLabel).join(", ")}
                  >
                    {localAdmins.map((admin) => (
                      <MenuItem key={admin.code} value={admin.code}>{admin.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    Accounts{" "}
                    <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>
                      {toArray(detail.utenti).length}{detail.accountLimit != null ? ` / ${detail.accountLimit}` : ""}
                    </Box>
                  </Typography>
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => handleAddAccount(detail.matricola)}>
                    Add account
                  </Button>
                </Box>

                {toArray(detail.utenti).length === 0 ? (
                  <Typography sx={{ color: MUTED }}>No user found</Typography>
                ) : (
                  <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px", overflow: "hidden" }}>
                    {toArray(detail.utenti).map((utente, idx) => (
                      <Stack
                        key={`${utente.email}-${utente.type}`}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ px: 1.5, py: 1, borderTop: idx === 0 ? "none" : `1px solid ${BORDER}` }}
                      >
                        <Avatar sx={{ width: 36, height: 36, fontSize: 15, fontWeight: 600, color: palette.primary.light, bgcolor: "rgba(69,144,149,0.14)" }}>
                          {utente.email?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontWeight: 600 }} noWrap>{utente.email}</Typography>
                          <Typography sx={{ fontSize: 12.5, color: MUTED }} noWrap>{utente.ragioneSociale || "-"}</Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={utente.type === "admin" ? "Primary" : "Secondary"}
                          sx={{
                            color: utente.type === "admin" ? palette.primary.light : MUTED,
                            backgroundColor: "transparent",
                            border: "1px solid",
                            borderColor: utente.type === "admin" ? palette.primary.main : "rgba(255,255,255,0.14)",
                          }}
                        />
                        <Tooltip title="Remove account" arrow>
                          <IconButton
                            size="small"
                            aria-label="Remove account"
                            onClick={() => handleRemoveAccount({ matricola: detail.matricola, email: utente.email, type: utente.type })}
                          >
                            <PersonRemoveOutlinedIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Drawer>

      {/* Dialog Add Device */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullScreen={isFullScreen}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { backgroundColor: SURFACE, backgroundImage: "none", border: `1px solid ${BORDER}`, borderRadius: "10px" } } }}
      >
        <DialogTitle sx={{ backgroundColor: SURFACE_HEAD, fontSize: 16, fontWeight: 700 }}>Add a new Device</DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <AddLecsReseller setOpen={setOpenDialog} />
        </DialogContent>
      </Dialog>

      {/* Dialog Aggiunta Account */}
      <AddAccountDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        matricola={selectedMatricola}
        onAccountAdded={fetchClients}
      />
    </Box>
  );
};

export default ClientList;
